/**
 * Alexia Shop - Cart Store
 * =========================
 * Pinia store for shopping cart state.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'
import type { Cart, CartItem, Product } from '@/types'

export const useCartStore = defineStore('cart', () => {
  // ============================================
  // STATE
  // ============================================
  const cart = ref<Cart | null>(null)
  const isLoading = ref(false)
  const isMiniCartOpen = ref(false)
  const error = ref<string | null>(null)

  // ============================================
  // GETTERS
  // ============================================
  const items = computed(() => cart.value?.items ?? [])
  const itemCount = computed(() => cart.value?.item_count ?? 0)
  const subtotal = computed(() => cart.value?.subtotal ?? 0)
  const total = computed(() => cart.value?.total ?? 0)
  const isEmpty = computed(() => itemCount.value === 0)

  // ============================================
  // ACTIONS
  // ============================================

  /**
   * Fetch cart from server
   */
  async function fetchCart(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get<Cart>('/cart')
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to load cart'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Add product to cart
   */
  async function addItem(productId: number, quantity: number = 1): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<Cart>('/cart/add', {
        product_id: productId,
        quantity,
      })
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
        // Show mini cart briefly
        openMiniCart()
        setTimeout(closeMiniCart, 3000)
        return true
      }
      error.value = response.data.message || 'Failed to add item'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to add item'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update item quantity
   */
  async function updateQuantity(itemId: number, quantity: number): Promise<boolean> {
    if (quantity < 1) {
      return removeItem(itemId)
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<Cart>('/cart/update', { item_id: itemId, quantity })
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
        return true
      }
      error.value = response.data.message || 'Failed to update quantity'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update quantity'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Remove item from cart
   */
  async function removeItem(itemId: number): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<Cart>('/cart/remove', { item_id: itemId })
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
        return true
      }
      error.value = response.data.message || 'Failed to remove item'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to remove item'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear all items from cart
   */
  async function clearCart(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.delete<Cart>('/cart')
      if (response.data.success) {
        cart.value = response.data.data ?? null
        return true
      }
      error.value = response.data.message || 'Failed to clear cart'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to clear cart'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Apply promo code
   */
  async function applyPromoCode(code: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<Cart>('/cart/promo', { code })
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
        return true
      }
      error.value = response.data.message || 'Invalid promo code'
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Invalid promo code'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Remove promo code
   */
  async function removePromoCode(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.delete<Cart>('/cart/promo')
      if (response.data.success && response.data.data) {
        cart.value = response.data.data
        return true
      }
      return false
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to remove promo code'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Mini cart toggle
  function openMiniCart(): void {
    isMiniCartOpen.value = true
  }

  function closeMiniCart(): void {
    isMiniCartOpen.value = false
  }

  function toggleMiniCart(): void {
    isMiniCartOpen.value = !isMiniCartOpen.value
  }

  function clearError(): void {
    error.value = null
  }

  return {
    // State
    cart,
    isLoading,
    isMiniCartOpen,
    error,
    // Getters
    items,
    itemCount,
    subtotal,
    total,
    isEmpty,
    // Actions
    fetchCart,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    applyPromoCode,
    removePromoCode,
    openMiniCart,
    closeMiniCart,
    toggleMiniCart,
    clearError,
  }
})
